import { SmileyIcon } from "@phosphor-icons/react";
import { countryCodes } from "apps/visits/packages/shared/static/country-codes";
import { Controller, FormProvider } from "react-hook-form";
import { useTranslation } from "react-i18next";
import InputMask from "react-input-mask";
import type { InferType } from "yup";
import { Select } from "@/core/shared/v2/ui/select";
import { SidebarLayout } from "@/core/shared/v2/ui/sidebar-layout";
import { DATE_FORMAT } from "../../config/date-time";
import { useGetConfig } from "../../store/use-config";
import { useOrderingStore } from "../../store/use-ordering-store";
import { Card } from "../card";
import { SideCart } from "../cart/side-cart";
import { Error } from "../error";
import { CheckboxField } from "../form-fields/checkbox-field";
import { SelectField } from "../form-fields/select-field";
import { TextField } from "../form-fields/text-field";
import { CheckboxesGroup } from "../time-select-form/checkboxes-group";
import { usePhoneVerificationForm } from "./use-phone-verification-form";
import { usePhoneVerificationLogic } from "./use-phone-verification-logic";
import type { useValidationSchema } from "./validation-schema";

export type PhoneVerificationFormValues = InferType<ReturnType<typeof useValidationSchema>>;

const FORM_ID = "phone-verification-form";

export const PhoneVerificationForm = () => {
  const { t } = useTranslation();
  const config = useGetConfig();
  const { personalDetails } = useOrderingStore();
  const { formMethods, watchIsDateOfBirth, agreementStates } = usePhoneVerificationForm();
  const { handleOnSubmit, handleOnInvalidSubmit, isLoading, generalError } = usePhoneVerificationLogic({
    formMethods,
    agreementStates,
  });

  return (
    <FormProvider {...formMethods}>
      <SidebarLayout>
        <Card>
          <form
            id={FORM_ID}
            onSubmit={formMethods.handleSubmit(handleOnSubmit, handleOnInvalidSubmit)}
            className="flex flex-col gap-5"
          >
            <div className="flex flex-col gap-6 md:grid md:grid-cols-2">
              <TextField
                ref={formMethods.register}
                name="firstName"
                required
                autoComplete="new-name"
                defaultValue={personalDetails?.firstName}
                label={t("GROUP_ACTIONS_ORDERING.STANDARD_REGISTRATION_FORM.FIRST_NAME")}
                error={formMethods.errors.firstName?.message}
              />
              <TextField
                ref={formMethods.register}
                name="lastName"
                required
                autoComplete="new-name"
                defaultValue={personalDetails?.lastName}
                label={t("GROUP_ACTIONS_ORDERING.STANDARD_REGISTRATION_FORM.LAST_NAME")}
                error={formMethods.errors.lastName?.message}
              />
            </div>
            <div className="flex gap-3">
              <div className="w-36">
                <SelectField
                  ref={formMethods.register}
                  name="dialCode"
                  required
                  defaultValue={personalDetails?.dialCode ?? "+48"}
                  label={t("GROUP_ACTIONS_ORDERING.PHONE_VERIFICATION_FORM.COUNTRY")}
                >
                  {[...countryCodes]
                    .sort((a, b) => a.dial_code.localeCompare(b.dial_code))
                    .map((country) => (
                      <Select.Option value={country.dial_code.replace(/ /g, "")} key={country.code}>
                        ({country.dial_code})
                      </Select.Option>
                    ))}
                </SelectField>
              </div>
              <div className="flex-1">
                <TextField
                  ref={formMethods.register}
                  name="phone"
                  required
                  inputMode="numeric"
                  defaultValue={personalDetails?.phone}
                  label={t("GROUP_ACTIONS_ORDERING.PHONE_VERIFICATION_FORM.PHONE_NUMBER")}
                  placeholder={t("GROUP_ACTIONS_ORDERING.PHONE_VERIFICATION_FORM.PHONE_NUMBER")}
                  error={formMethods.errors.phone?.message}
                />
              </div>
            </div>

            <div className="flex items-center gap-x-2">
              <span className="text-sm">{t("GROUP_ACTIONS_ORDERING.PHONE_VERIFICATION_FORM.PESEL_INFO")}</span>
              <SmileyIcon className="size-4" />
            </div>
            <Controller
              control={formMethods.control}
              name="isDateOfBirth"
              defaultValue={!!personalDetails?.dateOfBirth}
              render={({ onChange, value, ref }) => (
                <CheckboxField
                  ref={ref}
                  name="isDateOfBirth"
                  checked={!!value}
                  onCheckedChange={(checked) => onChange(!!checked)}
                  label={t("GROUP_ACTIONS_ORDERING.PHONE_VERIFICATION_FORM.PESEL_CHECKBOX_LABEL")}
                />
              )}
            />
            {!watchIsDateOfBirth && (
              <TextField
                ref={formMethods.register}
                name="pesel"
                defaultValue={personalDetails?.pesel}
                label={t("GROUP_ACTIONS_ORDERING.PHONE_VERIFICATION_FORM.PESEL")}
                placeholder={t("GROUP_ACTIONS_ORDERING.PHONE_VERIFICATION_FORM.PESEL")}
                error={formMethods.errors.pesel?.message}
                inputMode="numeric"
              />
            )}
            {watchIsDateOfBirth && (
              <Controller
                control={formMethods.control}
                name="dateOfBirth"
                defaultValue={personalDetails?.dateOfBirth}
                render={(field) => (
                  <InputMask
                    mask="99.99.9999"
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    maskPlaceholder={DATE_FORMAT}
                    placeholder={DATE_FORMAT}
                  >
                    <TextField
                      label={t("GROUP_ACTIONS_ORDERING.PHONE_VERIFICATION_FORM.DATE_OF_BIRTH")}
                      error={formMethods.errors.dateOfBirth?.message}
                      inputMode="numeric"
                    />
                  </InputMask>
                )}
              />
            )}
            <div className="mt-6">
              <CheckboxesGroup agreementStates={agreementStates} customAgreement={config.customAgreement} />
            </div>
          </form>
        </Card>
        <SideCart
          readOnly
          submitFormId={FORM_ID}
          buttonLabel={t("GROUP_ACTIONS_ORDERING.GENERAL.GO_NEXT")}
          ctaLoading={isLoading}
          appendedContent={<Error error={generalError} className="mt-2" />}
        />
      </SidebarLayout>
    </FormProvider>
  );
};
